/**
 * Exportable reports for a run — the Performance capacity report and the Security
 * findings report, plus the raw k6/ZAP summaries they were built from.
 *
 * A SEPARATE component from `Filmstrip`, deliberately. Reports were briefly folded
 * into the screenshot list — both are "files a run produced" — and that made every
 * report render as an `<img>` thumbnail: a broken-image icon for a JSON or HTML
 * document, with nothing to click to actually open or download it. Reported
 * 2026-08-18. A document needs a link, not a picture frame.
 */
import { useEffect, useState } from 'react';
import { api } from '../api';

interface Props {
  runId: string;
  finished: boolean;
}

const KIND_LABEL: Record<string, string> = {
  'performance-report.html': 'Performance report',
  'performance-report.json': 'Performance report (data)',
  'security-report.html': 'Security report',
  'security-report.json': 'Security report (data)',
  'k6-summary.json': 'k6 raw summary',
  'zap-report.json': 'ZAP raw findings',
  'perf-metrics.jsonl': 'Server metrics (sampled)',
  'recording.json': 'Recorded session',
};

/** A short, human label from the stored filename, falling back to it verbatim. */
function labelFor(label: string): string {
  const fileName = label.split(' › ').pop() ?? label;
  for (const [suffix, human] of Object.entries(KIND_LABEL)) {
    if (fileName.endsWith(suffix)) return human;
  }
  return label;
}

export function RunReports({ runId, finished }: Props) {
  const [reports, setReports] = useState<Array<{ index: number; label: string; contentType: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.reports(runId)
        .then((r) => { if (!cancelled) setReports(r.reports); })
        .catch(() => { /* not available yet */ });
    };
    load();
    // Same timing as the filmstrip: reports are collected once the container
    // exits, so one read while running finds nothing and a delayed re-read after
    // finishing is what actually populates this.
    const timer = finished ? undefined : setInterval(load, 4000);
    if (finished) setTimeout(load, 1500);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [runId, finished]);

  if (!reports.length) return null;

  return (
    <div className="surface p-4" data-testid="run-reports">
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="text-sm font-medium">Reports</h3>
        <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          Exportable — open in a new tab, or save from there. Kept for as long as the run is.
        </span>
      </div>

      <ul className="flex flex-col gap-1.5">
        {reports.map((report) => (
          <li key={report.index}>
            {/* A real link, not a button with a click handler — the browser's own
                "open in new tab" / "save link as" then just work, which is the whole
                point: a report is something you view or download, not a thumbnail. */}
            <a
              href={api.reportUrl(runId, report.index)}
              target="_blank"
              rel="noreferrer"
              data-testid={`run-report-${report.index}`}
              className="flex items-center gap-2 rounded px-2 py-1.5 text-sm underline hover:opacity-80"
            >
              <span aria-hidden style={{ color: 'hsl(var(--text-faint))' }}>
                {report.contentType.includes('html') ? '▤' : '{ }'}
              </span>
              {labelFor(report.label)}
              <span className="ml-auto text-xs no-underline" style={{ color: 'hsl(var(--text-faint))' }}>
                {report.contentType.includes('html') ? 'view' : 'json'}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
